import { useMemo, useState } from 'react';
import type { CategoryId, Product } from '../types/product';
import { products } from '../data/products';
import { capacityBuckets, priceBuckets, sortOptions } from '../data/filters';

export type SortId = (typeof sortOptions)[number]['id'];

export interface FilterState {
  brands: string[];
  price: string | null;
  capacity: string | null;
  inStock: boolean;
  onSale: boolean;
}

const emptyFilters: FilterState = { brands: [], price: null, capacity: null, inStock: false, onSale: false };

export function useProductFilters(category: CategoryId | undefined, dealsOnly: boolean) {
  const [filters, setFilters] = useState<FilterState>(emptyFilters);
  const [sort, setSort] = useState<SortId>('popular');

  const base = useMemo<Product[]>(
    () => products.filter((p) => (!category || p.category === category) && (!dealsOnly || !!p.compareAt)),
    [category, dealsOnly],
  );

  const brandOptions = useMemo(() => {
    const counts = new Map<string, number>();
    base.forEach((p) => counts.set(p.brand, (counts.get(p.brand) ?? 0) + 1));
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [base]);

  const showCapacity = base.some((p) => p.capacityWh > 0);

  const results = useMemo(() => {
    const price = priceBuckets.find((b) => b.id === filters.price);
    const cap = capacityBuckets.find((b) => b.id === filters.capacity);
    const list = base.filter(
      (p) =>
        (filters.brands.length === 0 || filters.brands.includes(p.brand)) &&
        (!price || (p.price >= price.min && p.price < price.max)) &&
        (!cap || (p.capacityWh >= cap.min && p.capacityWh < cap.max)) &&
        (!filters.inStock || p.stock !== 'preorder') &&
        (!filters.onSale || !!p.compareAt),
    );
    const sorted = [...list];
    if (sort === 'price-asc') sorted.sort((a, b) => a.price - b.price);
    if (sort === 'price-desc') sorted.sort((a, b) => b.price - a.price);
    if (sort === 'rating') sorted.sort((a, b) => b.rating - a.rating);
    if (sort === 'popular') sorted.sort((a, b) => b.reviews - a.reviews);
    return sorted;
  }, [base, filters, sort]);

  const toggleBrand = (brand: string) =>
    setFilters((f) => ({
      ...f,
      brands: f.brands.includes(brand) ? f.brands.filter((b) => b !== brand) : [...f.brands, brand],
    }));

  const activeCount =
    filters.brands.length + (filters.price ? 1 : 0) + (filters.capacity ? 1 : 0) + (filters.inStock ? 1 : 0) + (filters.onSale ? 1 : 0);

  return {
    filters,
    setFilters,
    toggleBrand,
    clearAll: () => setFilters(emptyFilters),
    sort,
    setSort,
    results,
    brandOptions,
    showCapacity,
    activeCount,
  };
}
